import { readdir, stat } from "node:fs/promises";
import { extname, join } from "node:path";

/**
 * Walk a directory tree and return every subdirectory that contains at
 * least `minEntries` markdown files at its top level. The root directory
 * itself is included if it qualifies.
 *
 * "At its top level" means `.md` files **directly inside** the directory
 * (not in its subdirectories) — this is what an `_INDEX.md` in that
 * directory would index.
 *
 * `README.md` and `_INDEX.md` files are not counted (they are not
 * indexable entries). `.md` files starting with prefixes in `skipPrefixes`
 * are also not counted.
 *
 * Hidden directories (`.foo`) are not descended into. `_foo` directories
 * are descended into — they may legitimately contain content
 * (`_HUB-*.md`, `_INDEX.md` of subdirectories).
 *
 * A missing root directory returns an empty list rather than throwing.
 *
 * Use case — splitting a giant flat `_INDEX.md` into per-folder
 * indexes: scan a tree, find the meaningful directories, write an
 * `_INDEX.md` in each.
 */
export async function findIndexableDirs(
  rootDir: string,
  options: {
    minEntries?: number;
    skipPrefixes?: readonly string[];
  } = {},
): Promise<readonly string[]> {
  const minEntries = options.minEntries ?? 1;
  const skipPrefixes = options.skipPrefixes ?? [];
  const out: string[] = [];
  await walk(rootDir, minEntries, skipPrefixes, out);
  return out;
}

async function walk(
  dir: string,
  minEntries: number,
  skipPrefixes: readonly string[],
  out: string[],
): Promise<void> {
  let entries;
  try {
    entries = await readdir(dir, { withFileTypes: true });
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return;
    throw e;
  }

  let mdCount = 0;
  const subdirs: string[] = [];
  for (const dirent of entries) {
    if (dirent.isDirectory()) {
      if (dirent.name.startsWith(".")) continue;
      subdirs.push(dirent.name);
      continue;
    }
    if (!dirent.isFile()) continue;
    if (extname(dirent.name) !== ".md") continue;
    if (dirent.name === "README.md" || dirent.name === "_INDEX.md") continue;
    const nameNoExt = dirent.name.slice(0, -3);
    if (skipPrefixes.some((p) => nameNoExt.startsWith(p))) continue;
    // Confirm it's a regular file (not a symlink to a directory, etc.).
    try {
      const info = await stat(join(dir, dirent.name));
      if (!info.isFile()) continue;
    } catch {
      continue;
    }
    mdCount++;
  }

  if (mdCount >= minEntries) {
    out.push(dir);
  }

  for (const name of subdirs) {
    await walk(join(dir, name), minEntries, skipPrefixes, out);
  }
}
